import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { trapTab } from '../ui/focus';
import { WEEKDAYS_SHORT, weekdayIndex } from './calendar';
import { addDays, formatEventRange, isClosed, warsawParts, type EventView } from './labels';
import { refreshEvents } from './refresh';
import './repeat.css';
import { DialogLayer } from '../ui/Dialog';
import { sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';
import { Notice } from '../ui/Notice';

/** At most this many new events in one series (the server says the same). */
export const MAX_REPEATS = 52;

/**
 * "Wydarzenie cykliczne" (owner 2026-10-21 06:58: "powtórzyło co tydzień albo miesiąc z możliwością skopiowania
 * sprzętówki do odznaczenia"): how often, until when, what is copied, and which of the days (a holiday is unticked).
 */
export type RepeatDraft = {
  every: 'week' | '2weeks' | 'month';
  monthly: 'day' | 'weekday' | 'lastWeekday';
  end: 'count' | 'until';
  count: number;
  until: string;
  copyPacklist: boolean;
  copyCrew: boolean;
  copyQuote: boolean;
  /** Days of the preview the person unticked. */
  skipped: ReadonlySet<string>;
};

export const emptyRepeat = (me: Me | null | undefined, firstDay: string): RepeatDraft => ({
  every: 'week',
  monthly: 'weekday',
  end: 'count',
  count: 4,
  until: firstDay ? addDays(firstDay, 91) : '',
  copyPacklist: !!me && can(me, 'packlists.lists', 'edit'),
  copyCrew: false,
  copyQuote: false,
  skipped: new Set(),
});

type PreviewDay = { day: string; shortages?: number };
type Preview = { days: PreviewDay[]; label: string };
export type SeriesResult = { seriesId: string; events: Array<{ id: string; firstDay: string; status: string; shortages: number; crewSkipped?: Array<{ personName: string; functionName: string }> }> };

/** The rule as the server takes it. */
const ruleOf = (d: RepeatDraft) => ({ every: d.every, ...(d.every === 'month' ? { monthly: d.monthly } : {}), ...(d.end === 'count' ? { count: d.count } : { until: d.until }) });
const ruleReady = (d: RepeatDraft) => (d.end === 'count' ? Number.isInteger(d.count) && d.count >= 1 : /^\d{4}-\d{2}-\d{2}$/.test(d.until));

/** The dates of the series, from the server (the same arithmetic it creates with), with shortages when known. */
export function useRepeatPreview(draft: RepeatDraft, from: { eventId: string } | { firstDay: string }) {
  const body = { ...from, ...ruleOf(draft) };
  return useQuery({
    queryKey: ['series', 'preview', body],
    enabled: ruleReady(draft) && ('eventId' in from ? !!from.eventId : /^\d{4}-\d{2}-\d{2}$/.test(from.firstDay)),
    queryFn: () => api<Preview>('/api/series/preview', { method: 'POST', body }),
  });
}

/** The days to create: the preview's, without the unticked ones. */
export const chosenDays = (draft: RepeatDraft, preview: Preview | undefined): string[] => (preview?.days ?? []).map((d) => d.day).filter((d) => !draft.skipped.has(d));

/** A key per opened form: the same request twice (a double click) makes the series once. */
export function requestKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Creates the series of `eventId` on `days`. */
export const createSeries = (eventId: string, draft: RepeatDraft, days: string[], key: string) =>
  api<SeriesResult>('/api/series', { method: 'POST', body: { eventId, days, ...ruleOf(draft), copyPacklist: draft.copyPacklist, copyCrew: draft.copyCrew, copyQuote: draft.copyQuote, requestKey: key } });

/** "Utworzono 4 kolejne wydarzenia (co tydzień). Braki sprzętu: 20.11." */
export function seriesNotice(result: SeriesResult): string {
  const n = result.events.length;
  const short = result.events.filter((e) => e.shortages > 0).map((e) => dayShort(e.firstDay));
  // review M1: people not copied — they no longer have the function (their places are free)
  const skipped = [...new Set(result.events.flatMap((e) => (e.crewSkipped ?? []).map((x) => `${x.personName} (${x.functionName})`)))];
  return (
    `Utworzono kolejne wydarzenia w serii: ${n}.` +
    (short.length > 0 ? ` Brakuje sprzętu: ${short.join(', ')} — sprawdź ich sprzętówki.` : '') +
    (skipped.length > 0 ? ` Nie skopiowano do obsady (nie mają już tej funkcji): ${skipped.join(', ')} — ich miejsca są wolne.` : '')
  );
}

/** Polish forms of "wydarzenie" after a number: 1 wydarzenie, 2–4 (22–24…) wydarzenia, else wydarzeń. */
export function eventsWord(n: number): string {
  if (n === 1) return 'wydarzenie';
  return n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 'wydarzenia' : 'wydarzeń';
}

/** "powstanie" / "powstaną" before a number of events (1 and 5+: powstanie, 2–4, 22–24…: powstaną). */
export const createdWord = (n: number): string => (eventsWord(n) === 'wydarzenia' ? 'powstaną' : 'powstanie');

/** The day is in the last 7 days of its month: "ostatni piątek" is offered (the server's rule is the same). */
export function isLastWeekOfMonth(day: string): boolean {
  const y = Number(day.slice(0, 4));
  const m = Number(day.slice(5, 7));
  return Number(day.slice(8, 10)) > new Date(Date.UTC(y, m, 0)).getUTCDate() - 7;
}

const WEEKDAY_ACC = ['poniedziałek', 'wtorek', 'środę', 'czwartek', 'piątek', 'sobotę', 'niedzielę'] as const;
const LAST_ACC = ['ostatni', 'ostatni', 'ostatnią', 'ostatni', 'ostatni', 'ostatnią', 'ostatnią'] as const;

const dayShort = (day: string) => `${Number(day.slice(8, 10))}.${day.slice(5, 7)}`;
const dayLong = (day: string) => `${WEEKDAYS_SHORT[weekdayIndex(day)]} ${dayShort(day)}.${day.slice(0, 4)}`;

/**
 * The repeat options and the preview of dates (each can be unticked). `from`: an existing event (shortages are
 * estimated) or the first day of one being made (the new event wizard, the new rental).
 */
export function RepeatFields({ draft, onChange, from, firstDay }: { draft: RepeatDraft; onChange: (d: RepeatDraft) => void; from: { eventId: string } | { firstDay: string }; /** The event's first day: "ostatni piątek" is offered in the last week of a month. */ firstDay: string }) {
  const { me } = useMe();
  const preview = useRepeatPreview(draft, from);
  if (!me) return null;
  const set = (patch: Partial<RepeatDraft>) => onChange({ ...draft, ...patch });
  const days = preview.data?.days ?? [];
  const chosen = chosenDays(draft, preview.data);
  const toggleDay = (day: string) => {
    const next = new Set(draft.skipped);
    if (next.has(day)) next.delete(day);
    else next.add(day);
    set({ skipped: next });
  };
  const error = preview.error instanceof ApiError ? preview.error : null;
  return (
    <div className="repeat-fields" data-feature-id="series.repeat">
      <div className="field-row">
        <label className="field">
          <span>Powtarzaj</span>
          <select value={draft.every} onChange={(e) => set({ every: e.target.value as RepeatDraft['every'], skipped: new Set() })}>
            <option value="week">co tydzień</option>
            <option value="2weeks">co 2 tygodnie</option>
            <option value="month">co miesiąc</option>
          </select>
        </label>
        {draft.every === 'month' && (
          <label className="field">
            <span>Którego dnia</span>
            <select value={draft.monthly} onChange={(e) => set({ monthly: e.target.value as RepeatDraft['monthly'], skipped: new Set() })}>
              <option value="weekday">ten sam dzień tygodnia (np. 2. piątek)</option>
              <option value="day">ten sam dzień miesiąca</option>
              {firstDay && isLastWeekOfMonth(firstDay) && <option value="lastWeekday">{`${LAST_ACC[weekdayIndex(firstDay)]} ${WEEKDAY_ACC[weekdayIndex(firstDay)]} miesiąca`}</option>}
            </select>
          </label>
        )}
      </div>
      <div className="field-row">
        <label className="field">
          <span>Koniec</span>
          <select value={draft.end} onChange={(e) => set({ end: e.target.value as RepeatDraft['end'], skipped: new Set() })}>
            <option value="count">po liczbie wydarzeń</option>
            <option value="until">w dniu</option>
          </select>
        </label>
        {draft.end === 'count' ? (
          <label className="field">
            <span>Ile kolejnych (najwyżej {MAX_REPEATS})</span>
            <input type="number" inputMode="numeric" min={1} max={MAX_REPEATS} value={draft.count} onChange={(e) => set({ count: Math.max(1, Math.min(MAX_REPEATS, Math.round(Number(e.target.value) || 1))), skipped: new Set() })} />
          </label>
        ) : (
          <label className="field">
            <span>Ostatni dzień</span>
            <input type="date" value={draft.until} onChange={(e) => set({ until: e.target.value, skipped: new Set() })} />
          </label>
        )}
      </div>
      <fieldset className="repeat-copies">
        <legend>Skopiuj do każdego</legend>
        <label className="check">
          <input type="checkbox" checked={draft.copyPacklist} disabled={!can(me, 'packlists.lists', 'edit')} onChange={(e) => set({ copyPacklist: e.target.checked })} />
          <span>Skopiuj sprzętówkę</span>
        </label>
        {can(me, 'crew.assignments', 'create') && (
          <label className="check">
            <input type="checkbox" checked={draft.copyCrew} onChange={(e) => set({ copyCrew: e.target.checked })} />
            <span>
              Skopiuj obsadę <span className="muted">(każdy jako „proponowany” — do potwierdzenia)</span>
            </span>
          </label>
        )}
        {can(me, 'quotes.quotes', 'create') && (
          <label className="check">
            <input type="checkbox" checked={draft.copyQuote} onChange={(e) => set({ copyQuote: e.target.checked })} />
            <span>
              Skopiuj kosztorys <span className="muted">(nowa wersja robocza)</span>
            </span>
          </label>
        )}
      </fieldset>
      {error && (
        <p className="alert" role="alert">
          {fieldIssues(error).map((i) => i.message).join(' ') || error.message}
        </p>
      )}
      {preview.data && (
        <div className="repeat-days">
          <p className="muted small">
            {preview.data.label[0]?.toUpperCase()}
            {preview.data.label.slice(1)} — {chosen.length === 0 ? 'nic nie zaznaczono' : `${createdWord(chosen.length)} ${chosen.length} ${eventsWord(chosen.length)}`}. Odznacz dni, w które nie gracie.
          </p>
          {days.length === 0 && <p className="muted">W tym czasie nie wypada żaden termin.</p>}
          <ul aria-label="Terminy serii">
            {days.map((d) => (
              <li key={d.day}>
                <label className="check">
                  <input type="checkbox" checked={!draft.skipped.has(d.day)} onChange={() => toggleDay(d.day)} />
                  <span>
                    {dayLong(d.day)}
                    {d.shortages ? <span className="short-text"> — brakuje sprzętu ({d.shortages} {d.shortages === 1 ? 'pozycja' : 'poz.'})</span> : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
      {preview.isLoading && <p className="muted small" role="status">Liczenie terminów…</p>}
    </div>
  );
}

/** "Powtórz…" in the event card's "…": the series made from this event. */
export function RepeatDialog({ event, onClose }: { event: EventView; onClose: () => void }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const firstDay = event.startsAt ? warsawParts(event.startsAt).day : '';
  const [draft, setDraft] = useState<RepeatDraft>(() => emptyRepeat(me, firstDay));
  const [key] = useState(requestKey);
  const preview = useRepeatPreview(draft, { eventId: event.id });
  const days = chosenDays(draft, preview.data);
  const create = useMutation({
    mutationFn: () => createSeries(event.id, draft, days, key),
    onSuccess: async () => {
      await refreshEvents(queryClient);
      await queryClient.invalidateQueries({ queryKey: ['series'] });
    },
  });
  const error = create.error instanceof ApiError ? create.error : null;
  const done = create.data;
  // owner 2026-10-23 21:14: a repetition set up and not created: Escape asks first
  const opening = useOpening(draft);
  const guardRef = useUnsavedGuard({
    dirty: !done && !sameValues(draft, opening),
    save: async () => {
      await create.mutateAsync();
      return true;
    },
  });
  return (
    <div className="dialog-backdrop">
      <div
        ref={guardRef}
        className="dialog panel repeat-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="repeat-title"
        data-feature-id="series.repeat"
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
        <DialogLayer onClose={onClose} busy={create.isPending} />
        <h2 id="repeat-title">Powtórz wydarzenie</h2>
        <p className="muted">
          „{event.name}” · {formatEventRange(event.startsAt, event.endsAt)}. Każde powtórzenie to osobne wydarzenie (własny status, kosztorys, obsada, faktury), połączone w serię.
        </p>
        {!done && <RepeatFields draft={draft} onChange={setDraft} from={{ eventId: event.id }} firstDay={firstDay} />}
        {done && (
          <div>
            <Notice>{seriesNotice(done)}</Notice>
            <ul className="repeat-done">
              {done.events.map((e) => (
                <li key={e.id}>
                  <Link to={`/wydarzenia?wydarzenie=${e.id}`} onClick={onClose}>
                    {dayLong(e.firstDay)}
                  </Link>
                  {e.shortages > 0 && <span className="short-text"> — brakuje sprzętu</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {error && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="actions">
          {!done && (
            <button className="btn primary" type="button" disabled={create.isPending || days.length === 0} onClick={() => create.mutate()}>
              {create.isPending ? 'Tworzenie…' : `Utwórz ${days.length} ${eventsWord(days.length)}`}
            </button>
          )}
          <button className="btn" type="button" onClick={onClose}>
            {done ? 'Zamknij' : 'Anuluj'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The repeat section of the new event wizard and the new rental: off until ticked; the series is made when the new
 * event's card is left (closed, another card, another screen — `settleCreated`), so the copies take the packlist as it was filled.
 */
export function RepeatSection({ on, onToggle, draft, onChange, firstDay }: { on: boolean; onToggle: (on: boolean) => void; draft: RepeatDraft; onChange: (d: RepeatDraft) => void; firstDay: string }) {
  return (
    <div className="group repeat-section">
      <label className="check">
        <input type="checkbox" checked={on} onChange={(e) => onToggle(e.target.checked)} />
        <span>
          Wydarzenie cykliczne <span className="muted">(powtórz co tydzień albo co miesiąc)</span>
        </span>
      </label>
      {on && firstDay && <RepeatFields draft={draft} onChange={onChange} from={{ firstDay }} firstDay={firstDay} />}
      {on && !firstDay && <p className="muted small">Najpierw podaj pierwszy dzień.</p>}
      {on && <p className="hint">Kolejne wydarzenia powstaną, gdy zamkniesz kartę nowego wydarzenia — z taką sprzętówką, jaką w niej przygotujesz.</p>}
    </div>
  );
}

/** When the new event's card is left (`settleCreated`): the series of the made event on the preview's days; the notice to show. */
export async function finishSeries(eventId: string, draft: RepeatDraft, firstDay: string, key: string): Promise<string> {
  try {
    const preview = await api<Preview>('/api/series/preview', { method: 'POST', body: { firstDay, ...ruleOf(draft) } });
    const days = chosenDays(draft, preview);
    if (days.length === 0) return '';
    return seriesNotice(await createSeries(eventId, draft, days, key));
  } catch (error) {
    return `Kolejnych wydarzeń nie utworzono (${error instanceof ApiError ? error.message : 'błąd połączenia'}). Użyj „Powtórz…” w karcie wydarzenia.`;
  }
}

type SeriesInfo = { series: null | { seriesId: string; index: number; total: number; previous: { id: string; name?: string; startsAt?: string } | null; next: { id: string; name?: string; startsAt?: string } | null } };

/** "Seria: 2 z 5" in the details, with the previous and the next event of the series. */
export function SeriesRow({ eventId }: { eventId: string }) {
  const info = useQuery({ queryKey: ['series', 'event', eventId], queryFn: () => api<SeriesInfo>(`/api/series/events/${eventId}`), retry: false });
  const s = info.data?.series;
  if (!s) return null;
  const link = (e: { id: string; startsAt?: string }, label: string) => (
    <Link to={`/wydarzenia?wydarzenie=${e.id}`} aria-label={`${label} wydarzenie serii${e.startsAt ? ` (${dayShort(warsawParts(e.startsAt).day)})` : ''}`}>
      {label === 'Poprzednie' ? '‹ ' : ''}
      {e.startsAt ? dayShort(warsawParts(e.startsAt).day) : label.toLowerCase()}
      {label === 'Następne' ? ' ›' : ''}
    </Link>
  );
  return (
    <div data-feature-id="series.card">
      <dt className="muted">Seria</dt>
      <dd className="series-row">
        <span>
          {s.index} z {s.total}
        </span>
        {s.previous && link(s.previous, 'Poprzednie')}
        {s.next && link(s.next, 'Następne')}
      </dd>
    </div>
  );
}

/** Whether "Powtórz…" is offered for the event. */
export const mayRepeat = (me: Me, e: EventView): boolean => can(me, 'events.events', 'create') && !isClosed(e.status) && !!e.startsAt;
